import React from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { newsSeverity } from 'types/timetableServices';

import { localeToLanguageKey } from 'utils/timetableServices';

import AlertBox from 'components/ui/AlertBox';

const alertType = {
  [newsSeverity.NORMAL]: AlertBox.variants.SUCCESS,
  [newsSeverity.MINOR]: AlertBox.variants.WARNING,
  [newsSeverity.MAJOR]: AlertBox.variants.ERROR,
  [newsSeverity.CRITICAL]: AlertBox.variants.ERROR
};

/**
 * @typedef {('NORMAL'|'MINOR'|'MAJOR'|'CRITICAL')} NewsSeverityEnum
 */

/**
 * @typedef {Object} TimetableTranslations
 * @property {string} de
 * @property {string} en
 * @property {string} fr
 * @property {string} nl
 */

/**
 * Banner News message component
 *
 * @param {Object} props
 * @param {NewsSeverityEnum} props.severity
 * @param {TimetableTranslations} props.commercial
 * @param {TimetableTranslations} props.description
 * @param {string} [props.detailsPath]
 * @param {TimetableTranslations} props.title
 * @param {Object} props.intl
 * @param {string} props.intl.locale
 */

const Message = ({
  alert,
  commercial: commercialTranslations,
  description: descriptionTranslations,
  detailsPath,
  id,
  severity,
  title: titleTranslations,
  intl: { locale }
}) => {
  const languageKey = localeToLanguageKey({ locale });

  const commercial = commercialTranslations[languageKey];
  const description = descriptionTranslations[languageKey];
  const title = titleTranslations[languageKey];

  const variant = alertType[severity];

  const idPrefix = `message-${id}`;

  const content = (
    <>
      <h3 data-testid={`${idPrefix}-title`}>{title}</h3>
      <p data-testid={`${idPrefix}-description`}>{description}</p>
      <p data-testid={`${idPrefix}-commercial`}>{commercial}</p>
      {detailsPath && (
        <Link data-testid={`${idPrefix}-find-out-more`} to={detailsPath}>
          <FormattedMessage id="travelUpdates.findOutMore" />
        </Link>
      )}
    </>
  );

  return alert ? <AlertBox variant={variant}>{content}</AlertBox> : <div>{content}</div>;
};

Message.propTypes = {
  alert: PropTypes.bool,
  commercial: PropTypes.shape({
    en: PropTypes.string,
    de: PropTypes.string,
    fr: PropTypes.string,
    nl: PropTypes.string
  }),
  description: PropTypes.shape({
    en: PropTypes.string,
    de: PropTypes.string,
    fr: PropTypes.string,
    nl: PropTypes.string
  }),
  detailsPath: PropTypes.string,
  id: PropTypes.string,
  severity: PropTypes.oneOf(Object.keys(newsSeverity)),
  title: PropTypes.shape({
    en: PropTypes.string,
    de: PropTypes.string,
    fr: PropTypes.string,
    nl: PropTypes.string
  }),
  intl: intlShape
};

export default injectIntl(Message);
